import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import getMenuData, { MenuDataImpl } from '../../api/menu'
import { selectAccessControl } from '../../redux/accessControls/slice'
import { checkUserHasViewPermission } from '../../helper/checkUserPermission'
import { UserRoles, roleDefaultPaths } from '../../redux/user/model'
import { ViewPermissions } from '../../types/AccessControls'
import { RootState } from '../../index'

/**
 * Finds the first menu item URL that the user has permission to access.
 */
const findFirstAccessibleUrl = (
  items: MenuDataImpl[],
  viewPermissions?: ViewPermissions,
): string | null => {
  for (const item of items) {
    if (item.children) {
      const childUrl = findFirstAccessibleUrl(item.children, viewPermissions)
      if (childUrl) return childUrl
    } else if (item.url) {
      const hasPermission = checkUserHasViewPermission(item.permissionLevel, viewPermissions)
      if (hasPermission) return item.url
    }
  }
  return null
}

const RedirectRootPath = () => {
  const history = useHistory()
  const accessControls = useSelector(selectAccessControl)
  const userRole = useSelector((state: RootState) => state.user.role) as UserRoles | null
  const viewPermissions = accessControls?.data?.viewPermissions

  useEffect(() => {
    // Role-specific default path override
    if (userRole && roleDefaultPaths[userRole]) {
      history.push(roleDefaultPaths[userRole] as string)
      return
    }

    // For all other roles, redirect to first accessible menu item
    if (!viewPermissions) return

    const menuData = getMenuData()
    const firstAccessibleUrl = findFirstAccessibleUrl(menuData, viewPermissions)

    if (firstAccessibleUrl) {
      history.push(firstAccessibleUrl)
    } else {
      history.push('/404')
    }
  }, [viewPermissions, history, userRole])

  return null
}

export default RedirectRootPath
